import React, { useState, useEffect } from 'react';
import { FiCheck, FiAlertCircle, FiEye, FiEyeOff, FiSave, FiMic } from 'react-icons/fi';
import { authService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';

const PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4o, GPT-4o Mini, GPT-4 Turbo',
    models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo'],
    defaultModel: 'gpt-4o-mini',
    docsUrl: 'https://platform.openai.com/api-keys',
    color: 'bg-emerald-50 border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'claude',
    name: 'Anthropic Claude',
    description: 'Claude Haiku, Sonnet, Opus',
    models: ['claude-haiku-4-5-20251001', 'claude-sonnet-4-6', 'claude-opus-4-8'],
    defaultModel: 'claude-haiku-4-5-20251001',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    color: 'bg-orange-50 border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Gemini 1.5 Flash, Pro, Ultra',
    models: ['gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-2.0-flash'],
    defaultModel: 'gemini-1.5-flash',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    color: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'mistral',
    name: 'Mistral AI',
    description: 'Mistral Small, Medium, Large',
    models: ['mistral-small-latest', 'mistral-medium-latest', 'mistral-large-latest'],
    defaultModel: 'mistral-small-latest',
    docsUrl: 'https://console.mistral.ai/api-keys/',
    color: 'bg-purple-50 border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
  },
  {
    id: 'cohere',
    name: 'Cohere',
    description: 'Command R, Command R+',
    models: ['command-r', 'command-r-plus'],
    defaultModel: 'command-r',
    docsUrl: 'https://dashboard.cohere.com/api-keys',
    color: 'bg-coral-50 border-gray-200',
    badge: 'bg-gray-100 text-gray-700',
  },
];

const ProviderCard = ({ provider, config, onSave, onSetDefault, isDefault }) => {
  const t = useT();
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(config?.model || provider.defaultModel);
  const [enabled, setEnabled] = useState(config?.enabled || false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const hasStoredKey = config?.hasKey;

  const handleSave = async () => {
    if (!apiKey && !hasStoredKey) {
      setError(t('settings.apiKeyRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(provider.id, { apiKey: apiKey || undefined, model, enabled });
      setSaved(true);
      setApiKey('');
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.response?.data?.message || t('settings.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border rounded-xl p-5 space-y-4 ${provider.color}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{provider.name}</h3>
              {isDefault && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-primary-100 text-primary-700 uppercase tracking-wide">
                  {t('settings.active')}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{provider.description}</p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className="text-xs text-gray-500">{t('settings.enabled')}</span>
          <div
            onClick={() => setEnabled((v) => !v)}
            className={`w-9 h-5 rounded-full transition-colors relative ${enabled ? 'bg-primary-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
        </label>
      </div>

      {/* API Key input */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {t('settings.apiKey')} {hasStoredKey && <span className="text-green-600 font-normal">{t('settings.storedKey')}</span>}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasStoredKey ? '••••••••••••••••' : `Enter ${provider.name} API key`}
              className="input w-full pr-9 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showKey ? <FiEyeOff size={14} /> : <FiEye size={14} />}
            </button>
          </div>
        </div>
        {error && (
          <p className="mt-1 flex items-center gap-1 text-xs text-danger-600">
            <FiAlertCircle size={11} /> {error}
          </p>
        )}
      </div>

      {/* Model selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings.model')}</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="input w-full text-sm"
        >
          {provider.models.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-1.5 text-sm py-1.5"
        >
          {saved ? <FiCheck size={14} /> : <FiSave size={14} />}
          {saving ? t('settings.saving') : saved ? t('settings.saved') : t('settings.save')}
        </button>
        {!isDefault && (
          <button
            onClick={() => onSetDefault(provider.id)}
            className="btn btn-ghost text-sm py-1.5"
          >
            {t('settings.setAsDefault')}
          </button>
        )}
        <a
          href={provider.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs text-primary-600 hover:underline"
        >
          {t('settings.getApiKey')}
        </a>
      </div>
    </div>
  );
};

// ─── ElevenLabs Voice Synthesis Card ────────────────────────────────────────

const ElevenLabsCard = ({ config, onSave }) => {
  const t = useT();
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const hasStoredKey = config?.hasKey;

  const handleSave = async () => {
    if (!apiKey && !hasStoredKey) {
      setError('Voice AI API key is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave('elevenlabs', { apiKey: apiKey || undefined, enabled: true });
      setSaved(true);
      setApiKey('');
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-xl p-5 space-y-4 bg-violet-50 border-violet-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
            <FiMic size={18} className="text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Voice AI</h3>
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-violet-100 text-violet-700 uppercase tracking-wide">
                Voice Synthesis
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Professional Voice Cloning &amp; Telugu Audio</p>
          </div>
        </div>
      </div>

      <div className="bg-violet-100/60 rounded-lg px-3 py-2 text-xs text-violet-800 leading-relaxed">
        Used for <strong>Script to Voice</strong> — train custom voices with your voice artists and generate broadcast-quality Telugu audio.
        This key is shared org-wide; all staff can generate audio once configured here.
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          API Key {hasStoredKey && <span className="text-green-600 font-normal">(stored — enter new to replace)</span>}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasStoredKey ? '••••••••••••••••' : 'Enter API key'}
              className="input w-full pr-9 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showKey ? <FiEyeOff size={14} /> : <FiEye size={14} />}
            </button>
          </div>
        </div>
        {error && (
          <p className="mt-1 flex items-center gap-1 text-xs text-danger-600">
            <FiAlertCircle size={11} /> {error}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-1.5 text-sm py-1.5"
        >
          {saved ? <FiCheck size={14} /> : <FiSave size={14} />}
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
        </button>
        <a
          href="https://elevenlabs.io/app/settings/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs text-primary-600 hover:underline"
        >
          Get API key →
        </a>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const AIProviderSettings = () => {
  const t = useT();
  const { user, setUser } = useAuthStore();
  const [providerConfigs, setProviderConfigs] = useState({});
  const [activeProvider, setActiveProvider] = useState('openai');
  const [loading, setLoading] = useState(true);

  const [elevenLabsConfig, setElevenLabsConfig] = useState({ hasKey: false });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authService.getProfile();
        const cfg = profile.apiConfiguration || {};
        setActiveProvider(cfg.provider || profile.aiProvider || 'openai');
        const configs = {};
        PROVIDERS.forEach((p) => {
          configs[p.id] = {
            model: cfg[p.id]?.model || p.defaultModel,
            enabled: cfg[p.id]?.enabled || false,
            hasKey: cfg[p.id]?.hasKey || false,
          };
        });
        setProviderConfigs(configs);
        setElevenLabsConfig({ hasKey: cfg.elevenlabs?.hasKey || false });
      } catch (_) {
        // fall through with defaults
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSave = async (providerId, data) => {
    const payload = {
      provider: activeProvider,
      providerConfig: { [providerId]: data },
    };
    const updated = await authService.configureAPI(payload);

    if (providerId === 'elevenlabs') {
      setElevenLabsConfig((prev) => ({
        ...prev,
        hasKey: data.apiKey ? true : prev.hasKey,
      }));
    } else {
      setProviderConfigs((prev) => ({
        ...prev,
        [providerId]: {
          ...prev[providerId],
          model: data.model,
          enabled: data.enabled,
          hasKey: data.apiKey ? true : prev[providerId]?.hasKey,
        },
      }));
    }
    if (updated) setUser(updated);
  };

  const handleSetDefault = async (providerId) => {
    await authService.configureAPI({ provider: providerId });
    setActiveProvider(providerId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        {t('settings.loadingProviders')}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.aiProvider')}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {t('settings.aiProviderSubtitle')}
        </p>
      </div>

      <div className="space-y-4">
        {PROVIDERS.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            config={providerConfigs[provider.id]}
            onSave={handleSave}
            onSetDefault={handleSetDefault}
            isDefault={activeProvider === provider.id}
          />
        ))}
      </div>

      {/* ── Voice Synthesis section ── */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Voice Synthesis</h2>
        <p className="text-sm text-gray-500 mb-4">
          Configure Voice AI for the Script to Voice feature. One key serves the entire organisation.
        </p>
        <ElevenLabsCard config={elevenLabsConfig} onSave={handleSave} />
      </div>
    </div>
  );
};
